import React, { useEffect, useState } from 'react';
import { Loader2, MessageCircleQuestion, Plus, Save, Trash2 } from 'lucide-react';

import { callingBehaviorConfigAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from '../ui/use-toast';
import { COMMON_PROFILE_FIELD_KEYS } from '../Automation/automationConstants';

// 2026-09-07 (Docs/TO_BE_IMPLEMENTED/AI_CALLING_PRE_CALL_QUESTIONS_PLAN.md):
// configurable pre-call questions for WhatsApp Calling -- what the AI asks
// during a call, and whether it asks once (remembers) or every time.
// Deliberately its own component, not folded into CallingVoiceSettings.tsx
// (that one is TTS/STT provider config only) -- same "one component per
// concern" split DigitalCompanionSettings.tsx already uses relative to
// this file. Rendered as a sibling section on the same "AI Calling" tab
// (see SettingsPage.tsx) so voice and call-behavior settings live
// together, per that plan's §3.5 recommendation.
//
// Ticketing on/off and "mention ticket number" intentionally do NOT
// appear here -- confirmed 2026-09-07: both are configured once, for
// text and calling together, on the Digital Companion settings page.

type QuestionAskMode = 'ask_once' | 'ask_every_time';

type CallingQuestion = {
  field_key: string;
  prompt_text: string;
};

type CallingBehaviorConfig = {
  question_ask_mode: QuestionAskMode;
  questions: CallingQuestion[];
  welcome_message: string;
  system_prompt_override: string;
};

const DEFAULT_CONFIG: CallingBehaviorConfig = {
  question_ask_mode: 'ask_once',
  questions: [],
  welcome_message: '',
  system_prompt_override: '',
};

// Same extraction as AutomationRuleEditor.tsx's formatErrorMessage
// (2026-09-07 fix there) -- a 422 validation failure returns `detail` as
// an array of Pydantic error objects, not a string; rendering that
// directly would crash with React error #31 ("Objects are not valid as a
// React child").
function formatErrorMessage(error: any): string {
  if (error?.response?.data) {
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
    }
    if (typeof error.response.data.detail === 'string') return error.response.data.detail;
    if (error.response.data.message) return error.response.data.message;
  }
  return error?.message || 'Failed to save settings';
}

export default function CallingBehaviorSettings() {
  const [config, setConfig] = useState<CallingBehaviorConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await callingBehaviorConfigAPI.getConfig();
        if (!cancelled) {
          setConfig({
            question_ask_mode: data?.question_ask_mode || 'ask_once',
            questions: Array.isArray(data?.questions) ? data.questions : [],
            welcome_message: data?.welcome_message || '',
            system_prompt_override: data?.system_prompt_override || '',
          });
        }
      } catch (err: any) {
        if (!cancelled) setError(formatErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addQuestion = () => {
    setConfig((c) => ({ ...c, questions: [...c.questions, { field_key: '', prompt_text: '' }] }));
  };

  const updateQuestion = (index: number, patch: Partial<CallingQuestion>) => {
    setConfig((c) => ({
      ...c,
      questions: c.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  };

  const removeQuestion = (index: number) => {
    setConfig((c) => ({ ...c, questions: c.questions.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Drop rows the admin started but never finished -- an empty
      // field_key/prompt_text pair would just fail backend validation
      // with a confusing error, and silently keeping it around isn't
      // useful either.
      const payload = {
        question_ask_mode: config.question_ask_mode,
        questions: config.questions
          .map((q) => ({ field_key: q.field_key.trim(), prompt_text: q.prompt_text.trim() }))
          .filter((q) => q.field_key && q.prompt_text),
        welcome_message: config.welcome_message.trim(),
        system_prompt_override: config.system_prompt_override.trim(),
      };
      const updated = await callingBehaviorConfigAPI.updateConfig(payload);
      setConfig({
        question_ask_mode: updated?.question_ask_mode || 'ask_once',
        questions: Array.isArray(updated?.questions) ? updated.questions : [],
        welcome_message: updated?.welcome_message || '',
        system_prompt_override: updated?.system_prompt_override || '',
      });
      toast({ title: 'Saved', description: 'Call behavior settings updated.' });
    } catch (err: any) {
      const msg = formatErrorMessage(err);
      setError(msg);
      toast({ title: 'Save failed', description: msg, variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Welcome message</Label>
            <Textarea
              value={config.welcome_message}
              onChange={(e) => setConfig((c) => ({ ...c, welcome_message: e.target.value }))}
              placeholder='Default: "Hello, this is Maya. How can I help you?"'
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              Spoken before anything else on every call. Leave blank to use the default — translated
              automatically for callers whose preferred language is set.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Personality</Label>
            <Textarea
              value={config.system_prompt_override}
              onChange={(e) => setConfig((c) => ({ ...c, system_prompt_override: e.target.value }))}
              placeholder="e.g. You are Maya, warm and reassuring. Keep answers brief and simple."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Tone and style for calls only, not WhatsApp text. Safety rules always apply regardless.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">When to ask configured questions</Label>
            <Select
              value={config.question_ask_mode}
              onValueChange={(v) => setConfig((c) => ({ ...c, question_ask_mode: v as QuestionAskMode }))}
            >
              <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ask_once">Ask once (remember after)</SelectItem>
                <SelectItem value="ask_every_time">Ask every time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end border-t border-border/60 pt-4">
            <Button onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageCircleQuestion className="h-4 w-4" />
              Questions
            </CardTitle>
            <CardDescription>
              Asked right after the welcome message, on every call with one still pending.
            </CardDescription>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addQuestion} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Add question
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {config.questions.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No questions configured — the AI won't ask anything before continuing the call.
            </p>
          )}

          <div className="space-y-2">
            {config.questions.map((q, index) => (
              <div key={index} className="rounded-md border border-border/60 bg-muted/10 p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr]">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Field key</Label>
                      <Input
                        list={`calling-question-${index}-field-key-suggestions`}
                        value={q.field_key}
                        onChange={(e) => updateQuestion(index, { field_key: e.target.value })}
                        placeholder="e.g. name, age"
                      />
                      <datalist id={`calling-question-${index}-field-key-suggestions`}>
                        {COMMON_PROFILE_FIELD_KEYS.map((key) => (
                          <option key={key} value={key} />
                        ))}
                      </datalist>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">What the AI says</Label>
                      <Input
                        value={q.prompt_text}
                        onChange={(e) => updateQuestion(index, { prompt_text: e.target.value })}
                        placeholder="e.g. Could I get your name, please?"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-5 h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeQuestion(index)}
                    aria-label="Remove question"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t border-border/60 pt-4">
            <span className="text-xs text-muted-foreground">
              Ticketing on/off is configured on the Digital Companion tab — it applies to calls and text
              together.
            </span>
            <Button onClick={handleSave} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
